import { useState, useMemo, useEffect } from 'react';
import { useShelterStore } from '../../../store/shelterStore';
import { useAuthStore } from '../../../store/authStore';
import { notifications } from '../../../lib/notifications';
import type { Shelter } from '../../../types/shelter';
import { IconSearch, IconPackage, IconCheck, IconHistory, IconX, IconUser } from '@tabler/icons-react';
import { SUPPLY_CATEGORIES } from '../../../types/shelter';

interface Props { shelter: Shelter; }

interface DistLog {
  id: string;
  itemName: string;
  qty: number;
  time: string;
  recipientName: string;
  category: string;
}

const CAT_COLORS: Record<string, string> = {
  bedding: '#3b82f6', food: '#22c55e', water: '#06b6d4', hygiene: '#8b5cf6',
  medical: '#ef4444', equipment: '#f97316', other: '#7B7D7F',
};

export default function ShelterPortalDistribution({ shelter }: Props) {
  const { activeIncident } = useAuthStore();
  const { supplies, survivors, loadSupplies, updateSupplyItem } = useShelterStore();

  const LOG_KEY = `sp-dist-log-${activeIncident?.id ?? 'x'}-${shelter.id}`;
  const [log, setLog] = useState<DistLog[]>(() => {
    try { return JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); } catch { return []; }
  });

  useEffect(() => { loadSupplies(); }, [loadSupplies]);
  useEffect(() => {
    try { localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(0, 200))); } catch { /* ignore */ }
  }, [log, LOG_KEY]);

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedSurvivor, setSelectedSurvivor] = useState<{ id: number; name: string } | null>(null);
  const [qty, setQty] = useState<Record<number, number>>({});
  const [distributing, setDistributing] = useState<number | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);

  // Checked-in survivors at this shelter
  const shelterSurvivors = useMemo(() =>
    survivors
      .filter(s => s.shelterId === String(shelter.id) && s.status === 'checked_in')
      .map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}`.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [survivors, shelter.id],
  );

  const filteredSurvivors = recipientSearch
    ? shelterSurvivors.filter(s => s.name.toLowerCase().includes(recipientSearch.toLowerCase()))
    : shelterSurvivors;

  const shelterSupplies = useMemo(() =>
    supplies
      .filter(s => String(s.shelterId) === String(shelter.id) && s.quantity > 0)
      .filter(s => !catFilter || s.category === catFilter)
      .filter(s => !search || s.itemName.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => a.itemName.localeCompare(b.itemName)),
    [supplies, shelter.id, catFilter, search],
  );

  const getQty = (id: number) => qty[id] ?? 1;

  const distribute = async (item: typeof shelterSupplies[0]) => {
    const q = getQty(item.id);
    if (q < 1) return;
    setDistributing(item.id);
    try {
      await updateSupplyItem(item.id, { quantity: item.quantity - q });
      const recipientName = selectedSurvivor?.name ?? 'Anonymous';
      const entry: DistLog = {
        id: `${Date.now()}`,
        itemName: item.itemName,
        qty: q,
        category: item.category,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        recipientName,
      };
      setLog(prev => [entry, ...prev]);
      setQty(prev => ({ ...prev, [item.id]: 1 }));
      notifications.show({ title: 'Distributed', message: `${q}× ${item.itemName} to ${recipientName}`, color: 'var(--color-success)' });
    } catch {
      notifications.show({ title: 'Error', message: 'Distribution failed.', color: 'var(--color-error)' });
    } finally {
      setDistributing(null);
    }
  };

  return (
    <div>
      {/* Recipient picker */}
      <div className="sp-card" style={{ marginBottom: 8 }}>
        <div className="sp-card-title">Recipient</div>
        {selectedSurvivor ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#eeeeff', border: '1.5px solid #4B3FFF',
            borderRadius: 4, padding: '8px 16px',
          }}>
            <IconUser size={16} color="#4B3FFF" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1B1C1C' }}>
              {selectedSurvivor.name}
            </span>
            <button
              onClick={() => setSelectedSurvivor(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F', padding: 0 }}
            >
              <IconX size={16} />
            </button>
          </div>
        ) : (
          <button
            className="sp-btn sp-btn-secondary"
            style={{ fontSize: 14, padding: '8px 16px' }}
            onClick={() => setShowRecipientPicker(true)}
          >
            <IconUser size={16} />
            Select resident (or leave for anonymous)
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
          <input className="sp-input" style={{ paddingLeft: 32, fontSize: 14 }} placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="sp-input sp-select" style={{ width: 130, fontSize: 14 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All types</option>
          {SUPPLY_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {/* Items */}
      {shelterSupplies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#939598', fontSize: 14 }}>
          <IconPackage size={32} style={{ opacity: 0.3, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
          No supplies available for this shelter.
        </div>
      ) : shelterSupplies.map(item => (
        <div className="sp-dist-item" key={item.id}>
          <div className="sp-dist-info">
            <div className="sp-dist-name">{item.itemName}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <span className="sp-dist-cat" style={{ color: CAT_COLORS[item.category] ?? '#7B7D7F' }}>{item.category}</span>
              <span style={{ fontSize: 12, color: '#939598' }}>· {item.quantity} in stock</span>
            </div>
          </div>
          <div className="sp-dist-qty-box">
            <button className="sp-dist-qty-btn" onClick={() => setQty(q => ({ ...q, [item.id]: Math.max(1, (q[item.id] ?? 1) - 1) }))}>−</button>
            <span className="sp-dist-qty-val">{getQty(item.id)}</span>
            <button className="sp-dist-qty-btn" onClick={() => setQty(q => ({ ...q, [item.id]: Math.min(item.quantity, (q[item.id] ?? 1) + 1) }))}>+</button>
          </div>
          <button
            onClick={() => distribute(item)}
            disabled={distributing === item.id || getQty(item.id) > item.quantity}
            style={{
              width: 40, height: 40, borderRadius: 4, border: 'none', cursor: 'pointer',
              background: distributing === item.id ? '#dcfce7' : '#4B3FFF', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            {distributing === item.id ? <IconCheck size={16} color="#16a34a" /> : <IconCheck size={16} />}
          </button>
        </div>
      ))}

      {/* Log toggle */}
      {log.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => setShowLog(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#4B3FFF', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}
          >
            <IconHistory size={14} /> {showLog ? 'Hide' : 'Show'} distribution log ({log.length})
          </button>

          {showLog && (
            <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              {log.slice(0, 30).map(entry => (
                <div key={entry.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 16px', borderBottom: '1px solid #f1f5f9', fontSize: 14,
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{entry.qty}× {entry.itemName}</div>
                    <div style={{ fontSize: 12, color: '#939598' }}>{entry.recipientName}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#939598' }}>{entry.time}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recipient picker drawer */}
      {showRecipientPicker && (
        <div className="sp-drawer-overlay" onClick={() => setShowRecipientPicker(false)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()}>
            <div className="sp-drawer-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="sp-drawer-title" style={{ marginBottom: 0 }}>Select Recipient</div>
              <button onClick={() => setShowRecipientPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
                <IconX size={20} />
              </button>
            </div>

            <div style={{ position: 'relative', marginBottom: 8 }}>
              <IconSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
              <input
                className="sp-input"
                style={{ paddingLeft: 34 }}
                placeholder="Search by name…"
                value={recipientSearch}
                onChange={e => setRecipientSearch(e.target.value)}
              />
            </div>

            {/* Anonymous option */}
            <div
              onClick={() => { setSelectedSurvivor(null); setShowRecipientPicker(false); setRecipientSearch(''); }}
              style={{
                padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: 8, color: '#7B7D7F', fontSize: 14,
              }}
            >
              <IconUser size={16} style={{ opacity: 0.4 }} />
              Anonymous / No selection
            </div>

            {filteredSurvivors.length === 0 && shelterSurvivors.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#939598', fontSize: 14 }}>
                No residents currently checked in.
              </div>
            ) : filteredSurvivors.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#939598', fontSize: 14 }}>
                No results.
              </div>
            ) : filteredSurvivors.map(s => (
              <div
                key={s.id}
                onClick={() => { setSelectedSurvivor(s); setShowRecipientPicker(false); setRecipientSearch(''); }}
                style={{
                  padding: '11px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: selectedSurvivor?.id === s.id ? '#eeeeff' : 'white',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: '#eeeeff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: '#4B3FFF', flexShrink: 0,
                }}>
                  {s.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1B1C1C' }}>{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
